import {produce} from 'immer';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';

import {Metric, MetricsRequest, MetricsResultsMap} from 'app_api/projects/metrics/types';
import {ControlBarAction, ControlBarActionItems} from 'common/query-builder/query-entry-section/constants';
import {
  Action,
  Calculation,
  EntryPart,
  FormulaItem,
  isFormulaEntry,
  MetricEntry,
  QueryEventEntry,
  QueryFormulaEntry,
  QueryMetricEntry,
  RowTypeComplexOption,
} from 'common/query-builder/query-entry-section/types';
import {getQueryEntryUpdateFromCalculation} from 'common/query-builder/query-entry-section/util';
import {FeatureGate, Permission, PRIMITIVE_MATH_TYPES, ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {createMetric, showMetricsApiErrorToast, updateMetric} from 'common/report/queries/app-api/metrics';
import {getProjectId} from 'common/report/util';
import {KEY_VALUES} from 'common/util/dom';
import {Model, ModelStatus} from 'common/util/model-resource/model-resource';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {FormulasMenuItem, FormulasMenuItemType} from 'common/query-builder/query-block/formulas-menu/types';
import {getEntries} from 'common/report/insights/util/events';
import {BehaviorShowClause, FormulaShowClause} from 'common/report/insights/util/types';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {getAlphaLabelFromIndex} from 'common/util';
import {getResolvedFormulaEntry, isModifiedShowClauseFormula} from 'common/util/formulas';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';
import {buildCreateEntityModalAttrs} from 'common/report/util/saved-entities';
import {DisplaySize} from 'common/components/tooltip/types';
import {TrackingEvent} from 'common/query-builder/query-entry-section/tracking';

import {BaseEntryRow} from '../base';
// eslint-disable-next-line import/no-cycle
import {FormulaEntryRowEl} from './template';
import {
  FormulaParseError,
  FormulaParseVariableError,
  getFormulaShowClauseTrackingProps,
  getMetricUpdateFromFormulaShowClause,
  getNextFormulaName,
  getReferencedMetricsFromEntries,
  getUpdatedFormulaAndMetrics,
  parseFormula,
  replaceFormulaReferences,
} from './utils';

const canSaveMetrics = (section: QueryEntrySection) =>
  section.isConnected &&
  section.getContext(`currentProject`).get()?.value?.hasFeatureFlag(ProjectFeatureFlag.SavedFormulas) &&
  section.getContext(`currentProject`).get()?.value?.hasPermission(Permission.WriteMetrics);

function getNewInlineEntry(section: QueryEntrySection): FormulaItem {
  const existingFormulaNames = section
    .getEntries()
    .filter((entry): entry is QueryFormulaEntry => {
      return (
        isFormulaEntry(entry) &&
        // if it's a placeholder formula entry, it won't have event yet
        Boolean(entry.event)
      );
    })
    .map((entry) => entry.event.name)
    .filter(Boolean);

  return {
    name: getNextFormulaName({existingFormulaNames}),
    definition: ``,
  };
}

export class FormulaEntryRow extends BaseEntryRow<QueryFormulaEntry> {
  template = FormulaEntryRowEl;

  actionHandlers = {
    [ControlBarAction.Save]: () => this.saveFormula(),
    [ControlBarAction.RollingAverage]: (value: TODO) => this.handleCalculationSelected(value.calculation),
    [ControlBarAction.Cumulative]: (value: TODO) => this.handleCalculationSelected(value.calculation),
  };

  get canSaveMetrics() {
    return canSaveMetrics(this.section);
  }

  /** FormulaShowClause is the type that get persisted in Insights bookmarks */
  getResolvedFormulaShowClause(formulaEntry: QueryFormulaEntry = this.entry): FormulaShowClause | null {
    if (this.section.isConnected === false) {
      return null;
    }
    const resolvedEntry = getResolvedFormulaEntry({
      formulaEntry,
      metricsResource: this.section.getContext(`metrics`),
    });

    return (
      resolvedEntry && {
        formula: resolvedEntry.event,
        isExpanded: formulaEntry.isExpanded,
        isHidden: formulaEntry.isHidden,
        measurement: {
          cumulative: resolvedEntry.cumulative,
          rolling: resolvedEntry.rolling,
          multiAttribution: resolvedEntry.multiAttribution,
        },
      }
    );
  }

  /** @todo pablo -- simplify this code by remove usage of formula show clause formats here */
  getFormulaShowClauseFromEntry(entry: QueryFormulaEntry): FormulaShowClause {
    return {
      formula: entry.event,
      isExpanded: entry.isExpanded,
      isHidden: entry.isHidden,
      measurement: {
        cumulative: entry.cumulative,
        rolling: entry.rolling,
      },
    };
  }

  getName(): string | null {
    return this.getResolvedFormulaShowClause()?.formula.name;
  }

  getDefinition(): string | null {
    return this.getResolvedFormulaShowClause()?.formula.definition;
  }

  get model(): Model<Metric> {
    if (this.entry.event?.id) {
      if (this.section.isConnected) {
        return this.section.getContext(`metrics`).getOne(this.entry.event.id);
      } else {
        return {status: ModelStatus.Loading};
      }
    } else {
      return null;
    }
  }

  get isLoadingModel() {
    return this.model?.status === ModelStatus.Loading;
  }

  getFormulaError() {
    return this.getFormulaValidationError() || this.entry.event?.error;
  }

  getReferencedEntries(): Array<QueryMetricEntry | MetricEntry> {
    const referencedMetrics = this.getResolvedFormulaShowClause()?.formula.referencedMetrics;
    if (referencedMetrics) {
      return getEntries({showClauses: referencedMetrics});
    }
    return null;
  }

  getFormulaValidationError(): string | null {
    if (this.isLoadingModel) {
      return null;
    }

    const formulaDefinition = this.getDefinition();

    if (formulaDefinition) {
      const entries = this.getReferencedEntries() || this.section.getEntries({appendPlaceholder: true});
      const labelsToEntries = Object.fromEntries(entries.map((entry, idx) => [getAlphaLabelFromIndex(idx), entry]));
      const validLabels = Object.entries(labelsToEntries)
        .filter(([_, entry]) => !isFormulaEntry(entry))
        .map(([label]) => label);

      try {
        parseFormula(formulaDefinition, validLabels);
      } catch (err) {
        if (err instanceof FormulaParseError) {
          if (err instanceof FormulaParseVariableError) {
            const entry = labelsToEntries[err.variable];

            if (entry && isFormulaEntry(entry)) {
              return `Cannot reference formulas in formulas: ${err.variable}`;
            }
          }

          return err.message;
        }

        // we can ignore non-parser errors as to not interrupt the UX. the formula will be validated again at query
        console.error(err);
      }
    }

    return null;
  }

  isSelectingFormula(): boolean {
    return this.entry.event === null;
  }

  isUnsavedFormula(): boolean {
    return isNil(this.entry.event?.id);
  }

  isSavedFormula() {
    return this.isUnsavedFormula() === false;
  }

  /**
   * Returns true when a saved formula has been edited
   */
  isModifiedFormula(): boolean {
    return isModifiedShowClauseFormula(this.entry.event);
  }

  /**
   * Checks if the formula has a validation error.
   */
  hasFormulaError(): boolean {
    return this.getDefinition() && this.getFormulaValidationError() !== null;
  }

  isPlaceholder(): boolean {
    return this.entry.event === null || this.getDefinition() === `` || this.hasFormulaError();
  }

  isIntermediatePlaceholder(): boolean {
    return this.hasFormulaError();
  }

  getSaveDisabledMessage(): string | null {
    let referencedMetrics: BehaviorShowClause[];
    if (this.isUnsavedFormula()) {
      const {name, definition} = this.entry.event;
      ({referencedMetrics} = getUpdatedFormulaAndMetrics(
        {name, definition},
        this.section.getEntries() as Array<QueryMetricEntry | MetricEntry>,
      ));
    } else {
      referencedMetrics = this.getResolvedFormulaShowClause()?.formula.referencedMetrics;
    }
    const usesMultiAttributionMetric = Boolean(referencedMetrics.filter((c) => c.measurement.multiAttribution).length);
    const usesCountUsersOnceMetric = Boolean(referencedMetrics.filter((c) => c.measurement.segmentMethod).length);
    if (usesMultiAttributionMetric) {
      return `Cannot save formula because it references metric(s) using attribution`;
    } else if (usesCountUsersOnceMetric) {
      return `Cannot save formula because it references metric(s) using Unique Users - First/Last Time`;
    }
    return null;
  }

  isSaveEnabled(): boolean {
    return !(this.isPlaceholder() || this.getSaveDisabledMessage());
  }

  overflowControls() {
    const saveDisabledMessage = this.getSaveDisabledMessage();

    return [
      // display the save action for inline formulas or
      // for saved formulas with modifications
      this.canSaveMetrics &&
        this.isPlaceholder() === false && {
          ...ControlBarActionItems[ControlBarAction.Save],
          ...(saveDisabledMessage && {
            tooltip: {
              text: saveDisabledMessage,
              size: DisplaySize.Medium,
            },
            isDisabled: true,
          }),
        },

      // hide action only for insights
      this.section.attr(`enable-hide-entry`) &&
        this.isPlaceholder() === false &&
        ControlBarActionItems[this.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],

      // calculation options
      ControlBarActionItems[ControlBarAction.RollingAverage]({
        currentRollingWindowSize: this.entry.rolling,
        dateUnit: this.section.attr(`date-unit`),
      }),
      ControlBarActionItems[ControlBarAction.Cumulative],
    ].filter(Boolean);
  }

  handleUpsertedFormula({
    results,
    isCreating,
    isDuplicating,
  }: {
    results: MetricsResultsMap;
    isCreating?: boolean;
    isDuplicating?: boolean;
  }): void {
    if (!results) {
      return;
    }

    const metric = Object.values(results)[0];
    this.section.getContext(`metrics`).update(results, {partialUpdate: false});

    const entry: QueryFormulaEntry = {
      type: ResourceType.Formula,
      event: {id: metric.id},
      isExpanded: isCreating || (this.entry.isExpanded ?? false),
      multiAttribution: this.entry.multiAttribution,
    };

    if (isCreating) {
      this.track(TrackingEvent.SaveFormula, {
        ...this.getTrackingProps(entry),
        'Save as New': isDuplicating,
      });
    } else {
      this.track(TrackingEvent.SaveFormulaChanges, this.getTrackingProps(entry));
    }

    this.section.updateEntries(Action.Edit, {
      index: this.index,
      entry,
      trackingProps: this.getTrackingProps(entry),
    });
  }

  async saveFormula(isDuplicating = false) {
    if (this.isSavedFormula() && isDuplicating === false) {
      return this.handleSaveModifications();
    }

    let {name, definition, referencedMetrics} = this.entry.event;

    /** when saving an inline formula, we take reference metrics from the main report */
    if (this.isModifiedFormula() === false) {
      const updatedDefinition = getUpdatedFormulaAndMetrics(
        {name, definition},
        this.section.getEntries() as Array<QueryMetricEntry | MetricEntry>,
      );

      referencedMetrics = updatedDefinition.referencedMetrics;
      definition = updatedDefinition.definition;
    }

    const metricEntity: MetricsRequest = {
      type: `formula`,
      name,
      description: ``,
      definition: {
        formula: {
          definition,
          referencedMetrics,
        },
        measurement: this.getResolvedFormulaShowClause().measurement,
      },
    };

    const result = await createEntityModalPromise(
      buildCreateEntityModalAttrs({
        projectId: parseInt(getProjectId(), 10),
        entityType: EntityType.Metric,
        modalOpen: true,
        isEditing: false,
        isDuplicating,
        entity: metricEntity,
        showShareManagement: this.section
          .getContext(`currentProject`)
          .get()
          ?.value?.getFeatureGate(FeatureGate.Sharing),
      }),
      {
        existingEntityNames: this.section
          .getContext(`metrics`)
          .getAllValues()
          .map((value) => value.name),
      },
    );

    if (result.didSave) {
      let results: MetricsResultsMap;
      try {
        results = await createMetric({
          ...metricEntity,
          name: result.name,
          description: result.description,
        });
      } catch {
        results = null;
        showMetricsApiErrorToast();
      }

      this.handleUpsertedFormula({results, isCreating: true, isDuplicating});

      if (result.didShare) {
        await promptSharing({
          type: SharableEntityType.Metric,
          id: Number(Object.keys(results)[0]),
          'project-id': Number(getProjectId()),
        });
      }
    }
  }

  static getNewEntry(section: QueryEntrySection): QueryFormulaEntry {
    return {
      type: ResourceType.Formula,
      event: canSaveMetrics(section) ? null : getNewInlineEntry(section),
      filters: [],
    };
  }

  /**
   * Turns this entry into an empty inline entry.
   */
  createNewInlineEntry() {
    this.section.updateEntries(Action.Edit, {
      index: this.index,
      entry: {
        type: ResourceType.Formula,
        event: getNewInlineEntry(this.section),
      },
    });
  }

  onDefinitionCommit(newDefinition: string) {
    if (newDefinition) {
      this.updateFormula({definition: newDefinition});
    } else if (!this.section.isStagedEntry(this.index)) {
      // remove the formula entry if its definition is edited to be the empty string.
      // the !isStagedEntry check is to limit this codepath to editing existing definition rather than newly
      // staged formula.
      this.section.updateEntries(Action.Remove, {index: this.index});
    }
    this.section.helpers.setActiveEntry(`close`, {index: this.index, part: EntryPart.Value});
  }

  handleSelect(item: FormulasMenuItem) {
    const isSelectedSavedFormula = item.type === FormulasMenuItemType.SavedFormula;
    let formulaItem: FormulaItem;

    if (isSelectedSavedFormula) {
      formulaItem = {id: item.id};
    } else {
      formulaItem = {
        ...getNewInlineEntry(this.section),
        definition: item.definition,
      };
    }

    const entry: QueryFormulaEntry = {
      type: ResourceType.Formula,
      event: formulaItem,
    };

    if (isSelectedSavedFormula) {
      this.track(TrackingEvent.SelectFormula, this.getTrackingProps(entry));
    }

    this.section.updateEntries(Action.Edit, {
      entry,
      index: this.index,
      trackingProps: this.getTrackingProps(entry),
    });
  }

  handleDefinitionKeydown(ev: KeyboardEvent) {
    if (ev.key === KEY_VALUES.ENTER) {
      ev.preventDefault();
      const inputEl = ev.currentTarget as HTMLInputElement;
      inputEl.blur();
    }
  }

  handleDefinitionInput(ev: InputEvent) {
    const inputEl = ev.currentTarget as HTMLInputElement;
    inputEl.value = inputEl.value.toUpperCase();
  }

  handleDefinitionBlur(ev: FocusEvent) {
    this.onDefinitionCommit((ev.currentTarget as HTMLInputElement).value);
  }

  handleDefinitionFocus() {
    if (this.isSavedFormula()) {
      return;
    }

    const {index: activeIndex, part: activePart} = this.section.state.openEntry;
    if (activeIndex === this.index && activePart === EntryPart.Value) {
      // The formula will already be openEntry if it was added as new formula and autofocused. In this
      // case, don't update to avoid an unnecessary re-render
      return;
    }
    this.section.helpers.setActiveEntry(`open`, {index: this.index, part: EntryPart.Value});
  }

  handleCalculationRemoved(): void {
    this.updateFormula({rolling: null, cumulative: null});
  }

  handleCalculationSelected(calculation: Calculation): void {
    this.updateFormula(getQueryEntryUpdateFromCalculation({originalEntry: this.entry, calculation}));
  }

  onReferencedMetricsUpdate(ev: CustomEvent<{entries: QueryEventEntry[]; formulaRefUpdates?: Record<string, string>}>) {
    const {entries, formulaRefUpdates} = ev.detail;
    let definition = this.getDefinition();

    // update formula references
    if (formulaRefUpdates) {
      definition = replaceFormulaReferences(definition, formulaRefUpdates);
    }

    this.updateFormula({
      referencedMetrics: getReferencedMetricsFromEntries(entries),
      definition,
    });
  }

  updateFormula(
    modifiedParams: Partial<
      Pick<FormulaItem, 'name' | 'definition' | 'referencedMetrics'> & Pick<QueryFormulaEntry, 'rolling' | 'cumulative'>
    >,
  ) {
    const resolvedFormulaClause = this.getResolvedFormulaShowClause();

    const entry: QueryFormulaEntry = produce(this.entry, (draft) => {
      draft.event = {
        ...resolvedFormulaClause.formula,
        ...pick(modifiedParams, [`name`, `definition`, `referencedMetrics`]),
      };
      Object.assign(draft, {
        ...resolvedFormulaClause.measurement,
        ...pick(modifiedParams, [`rolling`, `cumulative`]),
        isExpanded: true,
      });
    });

    this.section.updateEntries(Action.Edit, {
      entry,
      index: this.index,
      trackingProps: this.getTrackingProps(entry),
    });
  }

  handleResetModifications(): void {
    const savedFormula = this.model.value;
    this.track(TrackingEvent.ResetFormula, this.getTrackingProps(this.entry));

    // NOTE: we need to have this logic here, rather than higher-up with all the other multi-attribution
    // compatibility checks because if the formula is reset to be unique without count user once, one of the
    // higher-up compatibility checks would re-add count users once to this formula (the aforementioned
    // compatibility check adds count users once to metrics with multi-attribution and count type unique).
    let multiAttribution = this.entry.multiAttribution;
    if (
      multiAttribution &&
      // remove multi-attribution if the reset formula has count type unique without count user once
      savedFormula.definition.formula.referencedMetrics.some(
        (c) => c.measurement.math === PRIMITIVE_MATH_TYPES.UNIQUE && !c.measurement.segmentMethod,
      )
    ) {
      multiAttribution = null;
    }

    this.section.updateEntries(Action.Edit, {
      entry: produce(this.entry, (draft) => {
        const updates: Partial<QueryFormulaEntry> = {
          event: {
            // removing all fields except id will make this an unmodified saved formula
            id: savedFormula.id,
          },
          rolling: null,
          cumulative: null,
          multiAttribution,
        };
        Object.assign(draft, updates);
      }),
      index: this.index,
    });
  }

  async handleSaveModifications(saveAsNew = false): Promise<void> {
    if (saveAsNew) {
      return this.saveFormula(saveAsNew);
    }

    let results: MetricsResultsMap;
    try {
      results = await updateMetric({
        metricId: this.model.value.id.toString(),
        metric: getMetricUpdateFromFormulaShowClause(this.getResolvedFormulaShowClause()),
      });
    } catch {
      results = null;
      showMetricsApiErrorToast();
    }
    this.handleUpsertedFormula({results});
  }

  getReferencedMetricsRowTypes() {
    const types = this.section.attr(`row-types`);
    return types.filter(
      (option) =>
        (typeof option === `string` && option !== ResourceType.Formula) ||
        (option as RowTypeComplexOption).value !== ResourceType.Formula,
    );
  }

  getTrackingProps(entry: QueryFormulaEntry) {
    return getFormulaShowClauseTrackingProps({
      formulaShowClause: this.getFormulaShowClauseFromEntry(entry),
      metricsResource: this.section.getContext(`metrics`),
    });
  }

  handleToggleExpandRowClick() {
    this.section.toggleExpandedRow(this.index, this.getTrackingProps(this.entry));
  }

  track(event: TrackingEvent, extraProps: Obj = {}) {
    this.section.dispatchTrack(event, {
      ...this.section.getEntryTrackingProps(this.index, this.entry),
      ...extraProps,
    });
  }
}
